import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { ASSETS } from "@/data/content";

const SECTION_LINKS = [
  { key: "home", target: "hero" },
  { key: "about", target: "about" },
  { key: "brands", target: "brands" },
  { key: "b2b", target: "b2b" },
  { key: "quality", target: "quality" },
  { key: "clients", target: "clients" },
];

const Navbar = () => {
  const { t, lang, toggleLang, isRTL } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (target) => {
    setOpen(false);
    const scroll = () => {
      const el = document.getElementById(target);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
      else if (el) el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 400);
    } else {
      scroll();
    }
  };

  const goToRoute = (path) => {
    setOpen(false);
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#F5F2EB]/80 border-b border-[#E5DFD3] py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => goToSection("hero")}
            className="flex items-center gap-3 group"
            aria-label="Home"
          >
            <img
              src={ASSETS.companyLogo}
              alt="Al-Ezdhar & Al-Rafah"
              className="h-11 w-11 md:h-12 md:w-12 object-contain rounded-full bg-white/70 p-1 ring-1 ring-[#E5DFD3]"
            />
            <span className="hidden lg:block font-display text-base leading-tight text-[#1A1A1A] max-w-[240px]">
              {t.company.name}
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {SECTION_LINKS.map((l) => (
              <button
                key={l.key}
                data-testid={`nav-${l.key}`}
                onClick={() => goToSection(l.target)}
                className="relative text-sm text-[#2A2A2A] hover:text-[#6A1E27] transition-colors after:absolute after:-bottom-1 after:inset-x-0 after:h-px after:bg-[#6A1E27] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-center"
              >
                {t.nav[l.key]}
              </button>
            ))}
            <button
              data-testid="nav-careers"
              onClick={() => goToRoute("/careers")}
              className="text-sm text-[#2A2A2A] hover:text-[#6A1E27] transition-colors"
            >
              {t.nav.careers}
            </button>
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="lang-switch"
              onClick={toggleLang}
              className="flex items-center gap-1.5 text-sm text-[#2A2A2A] hover:text-[#6A1E27] transition-colors border border-[#E5DFD3] rounded-full px-3 py-1.5"
            >
              <Globe className="w-4 h-4" />
              {lang === "ar" ? "EN" : "ع"}
            </button>
            <button
              data-testid="nav-contact-btn"
              onClick={() => goToRoute("/contact")}
              className="hidden md:inline-flex bg-[#6A1E27] text-white text-sm px-5 py-2.5 hover:bg-[#4A151C] transition-colors"
            >
              {t.nav.contact}
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(true)}
              className="lg:hidden text-[#1A1A1A]"
              aria-label="Menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-[#F5F2EB] flex flex-col"
            initial={{ opacity: 0, x: isRTL ? -40 : 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRTL ? -40 : 40 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5DFD3]">
              <img src={ASSETS.companyLogo} alt="logo" className="h-11 w-11 object-contain rounded-full bg-white p-1" />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close">
                <X className="w-7 h-7 text-[#1A1A1A]" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-5 px-8">
              {SECTION_LINKS.map((l, i) => (
                <motion.button
                  key={l.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => goToSection(l.target)}
                  className="font-display text-3xl text-start text-[#1A1A1A]"
                >
                  {t.nav[l.key]}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                onClick={() => goToRoute("/careers")}
                className="font-display text-3xl text-start text-[#1A1A1A]"
              >
                {t.nav.careers}
              </motion.button>
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                onClick={() => goToRoute("/contact")}
                className="font-display text-3xl text-start text-[#6A1E27]"
              >
                {t.nav.contact}
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
